/*!
 * level.js — level() and xfade(): a fader for every sound, by its name
 * © 2026 Rob Canning — GPLv3
 *
 *     level(noise, 0, 12)              noise to silence over 12 beats -- it keeps
 *                                      running: level(noise, 1, 4) brings it back
 *     level([noise, bass], 0.5, 8)     several at once
 *     level(all, 0, 24, exp3)          every sound, along a curve
 *     level(bass, 0)                   at once -- before synth(uid:bass ...),
 *                                      bass starts silent
 *     xfade(noise, bass, 8)            noise down to 0 and bass up to 1, together
 *     level(uid:[a, b], to:0, dur:3, curve:log)
 *
 * A level going up on a sound not yet heard fades it in from silence:
 *
 *     synth(uid:noise, wave:pink, amp:1)
 *     level(noise, 1, 12, exp3)           noise fades in over 12 beats
 *
 * -- in either order: level() before the sound waits for it to start; just
 * after (the same run: under half a second, no voice of it before) it
 * takes the fader down to 0 and brings it up from there.
 *
 * The level multiplies what the sound's own amp makes: 1 as written, 0
 * silent, up to 2. It belongs to the name, not to one voice: poly and pool
 * voices, and the voice a line run again makes, all go through it. hush()
 * stops sounds and puts their faders back to 1, so the next one is heard.
 *
 * Times are beats of tempo() (beats.js): at 60 bpm, the default, seconds.
 *
 * Curves are follow()'s -- lin (the default), exp2, exp3, exp4, log, sqrt --
 * bent so the quiet end lasts: exp3 falls fast and lingers low going down,
 * starts slowly coming up, as fades are heard. xfade is equal power unless
 * a curve is given, so the middle does not dip.
 */

import { applyCurve } from "../../control/paramBinding.js";
import { CURVE_NAMES } from "../../control/curves.js";
import { secs } from "../beats.js";
import { isRel, applyRel } from "../relative.js";
import { set as busSet } from "../../control/paramBus.js";
import { activeSynthVoices, activeAudioCues } from "./voices.js";

const MAX = 2;
const levels = new Map();          // name -> the level it is going to
const channels = new Map();        // name -> Map(destination node -> GainNode)
const armed = new Map();           // name -> { to, dur, curve }: a fade-in waiting for the sound
const startedAt = new Map();       // name -> ms (performance.now) it started, not playing before
const routes = new Map();          // name -> the node it goes to instead (fx(room, [a1]): the room, fxBus.js)
const routeVia = new Map();        // name -> the bus (its name) it goes through: solo() keeps that open
const JUST_STARTED_MS = 500;

const nameOf = key => String(key ?? "").split("__")[0];

// <name>.fader where the fader is going from: said at once when it is set,
// so a follow(noise.fader) bound next (viz(opacity:)) starts there and not
// at what the bus last held -- 1 from the run before, a flash. liveSignals
// says it every frame while the sound plays.
const sayFader = (name, v) => busSet(`${name}.fader`, v, { source: "level" });

/**
 * Where a voice of `key` (bass, bass__2 ...) goes instead of `dest`: its
 * name's fader, made on first use and kept.
 */
export function channelFor(key, dest) {
  const name = nameOf(key);
  if (!name || !dest) return dest;
  // a voice of a name with none playing: the sound starts (the new voice is
  // not yet in the registry when its graph is made)
  const fresh = !isSounding(name);
  if (fresh) startedAt.set(name, performance.now());
  let byDest = channels.get(name);
  if (!byDest) channels.set(name, byDest = new Map());
  let g = byDest.get(dest);
  if (!g) {
    g = dest.context.createGain();
    g.gain.value = levels.get(name) ?? 1;
    // after the fader, the solo stage (solo(), soloAt): 1, or 0 / boost while a solo holds
    g._solo = dest.context.createGain();
    g._solo.gain.value = soloFactor(name);
    // and the solo moves laid for later: a sound started by solo() before
    // its cut (quant:), or any made in between, keeps to them
    for (const e of soloTimers) soloRamp(g._solo, soloTarget(e, name), e.at, e.dur);
    g.connect(g._solo).connect(routes.get(name) ?? dest);
    for (const s of sends.get(name)?.values() || []) g._solo.connect(s);     // its sends (sendName)
    byDest.set(dest, g);
  }
  // a fade-in was waiting for it: from silence, now
  const fade = fresh && armed.get(name);
  if (fade) {
    armed.delete(name);
    for (const c of byDest.values()) jumpTo(c, 0);
    setLevel(name, fade.to, fade.dur, fade.curve);
  }
  return g;
}

/**
 * Send every voice of `name` -- those sounding, and those to come -- to
 * `node` (an fx bus's input) instead of where it would go; null sends it
 * back. The name's fader is the one place all its voices pass, so moving
 * it moves them all.
 */
// name -> Map(node -> gain): a copy of every voice of it, after its fader,
// into node (an fx bus's input) as well as where it goes (send:, synth.js)
const sends = new Map();

/**
 * Send every voice of `key` -- those sounding and those to come -- into
 * `node` as well, at `amount`; `node` null takes all its sends away. The
 * send's gain, which chance: opens and shuts step by step (synth.js).
 */
export function sendName(key, node, amount = 1) {
  const name = nameOf(key);
  if (!name) return null;
  let byNode = sends.get(name);
  if (!node) {
    for (const s of byNode?.values() || []) { try { s.disconnect(); } catch { /* gone */ } }
    sends.delete(name);
    return null;
  }
  if (!byNode) sends.set(name, byNode = new Map());
  let s = byNode.get(node);
  if (!s) {
    s = node.context.createGain();
    s.connect(node);
    byNode.set(node, s);
    for (const g of channels.get(name)?.values() || []) g._solo.connect(s);
  }
  try { s.gain.cancelScheduledValues(0); } catch { /* none */ }
  s.gain.value = amount;
  return s;
}

/** A bus taken away: the sends into its input go with it. */
export function unsendNode(node) {
  for (const [name, byNode] of sends) {
    const s = byNode.get(node);
    if (!s) continue;
    try { s.disconnect(); } catch { /* gone */ }
    byNode.delete(node);
    if (!byNode.size) sends.delete(name);
  }
}

export function routeName(key, node, via = null) {
  const name = nameOf(key);
  if (!name) return;
  const was = routeVia.get(name);
  if (node && via) routeVia.set(name, nameOf(via)); else routeVia.delete(name);
  if (node) { if (routes.get(name) === node) return; routes.set(name, node); }
  else { if (!routes.has(name)) return; routes.delete(name); }
  for (const [dest, g] of channels.get(name) || []) {
    try { g._solo.disconnect(); } catch { /* not connected */ }
    g._solo.connect(node ?? dest);
    for (const s of sends.get(name)?.values() || []) g._solo.connect(s);   // its sends kept
  }
  // a solo holding (or laid): the bus it now goes through opened, the one it left shut
  for (const bus of new Set([was, routeVia.get(name)])) if (bus) soloRelay(bus);
}

// ---- solo(): everything else cut, one sound alone -----------------------
//
// A stage of its own after each name's fader, so a solo never moves a
// level(): cut and back, each sound is where its fader left it. The moves
// are laid on the audio clock at the moment given (a beat ahead, quant:),
// fast (5 ms: a cut, not a click) or over fade:.

let solo = null;                   // { names: Set, boost } -- in force now (for channels made during it)

/** The solo stage's value for `name` now: 1 with no solo, else boost or 0. */
export function soloFactor(name) {
  if (!solo) return 1;
  return soloTarget({ set: solo.names, boost: solo.boost, quiet: new Set() }, nameOf(name));
}

/**
 * Does `name` pass sound the soloed ones make, or carry them? A bus a soloed
 * sound goes through (synth(…, bus:room) -- cut, the sound is cut with it),
 * or a sound going into a soloed bus (solo(room): what is in it).
 */
function soloPath(set, name) {
  const via = n => { const seen = new Set(); const out = []; while ((n = routeVia.get(n)) && !seen.has(n)) { seen.add(n); out.push(n); } return out; };
  for (const n of set) if (via(n).includes(name)) return true;
  return via(name).some(b => set.has(b));
}

/** Where solo stage `s` is at audio time t, from the moves given it here. */
function soloAt(s, t) {
  const m = s._move;
  if (!m) return s.gain.value;
  if (t <= m.t0) return m.from;
  if (t >= m.t0 + m.dur) return m.to;
  return m.from + (m.to - m.from) * (t - m.t0) / m.dur;
}

function soloRamp(s, to, at, dur) {
  const from = soloAt(s, at);
  try {
    s.gain.cancelScheduledValues(at);
    s.gain.setValueAtTime(from, at);
    s.gain.linearRampToValueAtTime(to, at + dur);
    s._move = { t0: at, dur, from, to };
  } catch { /* gone */ }
}

/**
 * Lay a solo on every channel at audio time `at`: `names` alone (at
 * `boost`), the rest at 0 -- or, names null, everything back to 1 (but
 * `silent`, the sounds the solo started, to 0) -- over `dur` seconds.
 * What was laid for `at` or later before is replaced; `solo` (for channels
 * made from then) follows when the moment comes.
 */
const soloTimers = [];             // [{ at, timer }]: the solo state changing then
const soloTarget = (e, name) => (e.set ? (e.set.has(name) ? e.boost : soloPath(e.set, name) ? 1 : 0) : (e.quiet.has(name) ? 0 : 1));

/** `name`'s solo stage laid again, now and for the moves to come (its way through a bus changed). */
function soloRelay(name) {
  if (!solo && !soloTimers.length) return;
  for (const g of channels.get(name)?.values() || []) {
    if (!g._solo) continue;
    soloRamp(g._solo, soloFactor(name), g._solo.context.currentTime, 0.005);
    for (const e of [...soloTimers].sort((a, b) => a.at - b.at)) soloRamp(g._solo, soloTarget(e, name), e.at, e.dur);
  }
}
export function setSolo(names, at, dur = 0.005, boost = 1, silent = []) {
  const set = names ? new Set(names.map(nameOf)) : null;
  const quiet = new Set(silent.map(nameOf));
  dur = Math.max(0.002, dur);
  for (const [name, byDest] of channels) {
    const to = soloTarget({ set, boost, quiet }, name);
    for (const g of byDest.values()) if (g._solo) soloRamp(g._solo, to, at, dur);
  }
  // a state change laid for later is replaced by this one (its ramps went with cancelScheduledValues)
  for (let i = soloTimers.length - 1; i >= 0; i--) {
    if (soloTimers[i].at >= at) { clearTimeout(soloTimers[i].timer); soloTimers.splice(i, 1); }
  }
  const ctx = [...channels.values()][0]?.values().next().value?.context;
  const wait = ctx ? Math.max(0, (at - ctx.currentTime) * 1000) : 0;
  const entry = { at, dur, set, boost, quiet, timer: setTimeout(() => {
    solo = set ? { names: set, boost } : null;
    soloTimers.splice(soloTimers.indexOf(entry), 1);
  }, wait) };
  soloTimers.push(entry);
}

/**
 * The sounds a solo started and still holds: run its line again yourself
 * (cueDispatcher) and it is yours -- the solo's end no longer stops it.
 */
export const soloOwned = new Set();

/** Lay `names`' solo stages to 0 at audio time `at`, over `dur` (a started sound going with the solo). */
export function soloQuiet(names, at, dur) {
  for (const name of names.map(nameOf)) {
    for (const g of channels.get(name)?.values() || []) if (g._solo) soloRamp(g._solo, 0, at, Math.max(0.002, dur));
  }
}

/** `names`' solo stages back to where the solo now says (1 with none), from now: a quieted one gone. */
export function soloRestore(names) {
  for (const name of names.map(nameOf)) {
    for (const g of channels.get(name)?.values() || []) {
      if (!g._solo) continue;
      const now = g._solo.context.currentTime;
      soloRamp(g._solo, soloFactor(name), now, 0.005);
    }
  }
}

/** Is any voice of `name` sounding (bass, bass__2 ...)? */
export function isSounding(name) {
  for (const k of activeSynthVoices.keys()) if (nameOf(k) === name) return true;
  for (const k of activeAudioCues.keys()) if (nameOf(k) === name) return true;
  return false;
}

/**
 * level() and xfade() move a fader through here: going up on a sound not
 * yet heard, it fades in from silence -- armed until the sound starts, or,
 * if it has only just started, from 0 now.
 */
function moveLevel(name, to, dur, curve) {
  name = nameOf(name);
  const rising = Number(to) > 0 && dur > 0;
  if (rising && !isSounding(name)) {
    armed.set(name, { to, dur, curve });
    levels.set(name, 0);
    sayFader(name, 0);
    for (const g of channels.get(name)?.values() || []) jumpTo(g, 0);
    return;
  }
  if (rising && performance.now() - (startedAt.get(name) ?? -Infinity) < JUST_STARTED_MS) {
    for (const g of channels.get(name)?.values() || []) {
      rampTo(g, 0, GLIDE);
    }
    startedAt.delete(name);
    sayFader(name, 0);
    // the drop to 0 takes a few ms: the fade starts from there
    setTimeout(() => setLevel(name, to, dur, curve), 20);
    return;
  }
  setLevel(name, to, dur, curve);
}

/** The fader's value now, for meters (vu.js): 1 when none was set. */
export function levelOf(key) {
  const byDest = channels.get(nameOf(key));
  if (!byDest?.size) return levels.get(nameOf(key)) ?? 1;
  return byDest.values().next().value.gain.value;
}

/** The path from `from` to `to` over n points, bent so the quiet end lasts. */
export function levelCurve(from, to, curve = "lin", n = 64) {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const p = i / (n - 1);
    if (curve === "sin" || curve === "cos") {
      // equal power: up along sin, down along cos
      const q = curve === "sin" ? Math.sin(p * Math.PI / 2) : Math.cos(p * Math.PI / 2);
      out[i] = curve === "sin" ? from + (to - from) * q : to + (from - to) * q;
    } else if (to >= from) {
      out[i] = from + (to - from) * applyCurve(p, curve);
    } else {
      out[i] = to + (from - to) * applyCurve(1 - p, curve);
    }
  }
  return out;
}

// A move starts this far ahead on the audio clock -- never inside a block
// already played -- and lasts at least GLIDE: the fader never jumps.
const LEAD = 0.01, GLIDE = 0.005;

/**
 * Where fader `g` is at audio time t, from the moves given it here. Read
 * from the node instead, it lags the audio thread: a new fade begun from
 * there, over one still running, jumped -- a click.
 */
function faderAt(g, t) {
  const m = g._move;
  if (!m) return g.gain.value;
  if (t <= m.t0) return m.from;
  if (t >= m.t0 + m.dur) return m.to;
  const k = (t - m.t0) / m.dur * (m.shape.length - 1), i = Math.floor(k);
  const a = m.shape[i], b = m.shape[Math.min(i + 1, m.shape.length - 1)];
  return a + (b - a) * (k - i);
}

/** Set a fader outright, when nothing is heard through it yet. */
function jumpTo(g, v) {
  try { g.gain.cancelScheduledValues(0); g.gain.value = v; } catch { /* gone */ }
  g._move = null;
}

/** Move fader `g` to `to` over `dur` seconds, along `curve`, from exactly where it is. */
function rampTo(g, to, dur = 0, curve = "lin") {
  const p = g.gain, t0 = g.context.currentTime + LEAD;
  const from = faderAt(g, t0);
  dur = Math.max(GLIDE, Number(dur) || 0);
  const shape = levelCurve(from, to, dur > GLIDE ? curve : "lin");
  try {
    // The curve is laid as short straight ramps, not setValueCurveAtTime:
    // Chrome's cancelAndHoldAtTime cuts a ramp where it is, but cutting into
    // a value curve the gain leapt back towards the curve's start for the
    // few ms before the hold -- the click when a fade was turned round.
    if (p.cancelAndHoldAtTime) p.cancelAndHoldAtTime(t0); else p.cancelScheduledValues(t0);
    p.setValueAtTime(from, t0);
    const n = shape.length - 1;
    for (let i = 1; i <= n; i++) p.linearRampToValueAtTime(shape[i], t0 + dur * i / n);
    g._move = { t0, dur, from, to, shape };
  } catch (err) {
    // a browser that will not lay the curve there: glide instead
    try { p.setTargetAtTime(to, t0, dur / 3); } catch { /* gone */ }
    g._move = null;
    console.warn("[level]", err);
  }
}

/** Move the fader of `name` to `to` over `dur` seconds. */
export function setLevel(name, to, dur = 0, curve = "lin") {
  name = nameOf(name);
  to = Math.max(0, Math.min(MAX, Number(to)));
  if (!name || !Number.isFinite(to)) return;
  levels.set(name, to);
  for (const g of channels.get(name)?.values() || []) rampTo(g, to, dur, curve);
}

/** Every sound by name: those playing, and those given a level before. */
function allNames() {
  const names = new Set(channels.keys());
  for (const k of activeSynthVoices.keys()) names.add(nameOf(k));
  for (const k of activeAudioCues.keys()) names.add(nameOf(k));
  return [...names];
}

/** hush(): the faders of the sounds named (or every one) back to 1. */
export function resetLevels(names = null) {
  for (const n of names ? names.map(nameOf) : [...armed.keys()]) armed.delete(n);
  for (const n of names ? names.map(nameOf) : [...levels.keys()]) {
    levels.delete(n);
    sayFader(n, 1);
    for (const g of channels.get(n)?.values() || []) {
      jumpTo(g, 1);
    }
  }
}

/** level(...) / xfade(...) arguments: { names, curve, numbers, to, dur }. */
export function levelArgs(args = []) {
  const get = k => args.find(a => a.type === k)?.value;
  const words = [...[].concat(get("names") ?? []), ...[].concat(get("uid") ?? [])].map(String);
  const curve = get("curve") ?? words.find(w => CURVE_NAMES.has(w));
  const names = words.filter(w => !CURVE_NAMES.has(w));
  return {
    names, curve: curve != null ? String(curve) : null,
    // a relative to: level(agents, *0.5) -- kept as it is, each fader applies it (handleLevelCue)
    numbers: [].concat(get("numbers") ?? []).map(n => (n && typeof n === "object" ? n : Number(n))),
    to: get("to"), dur: get("dur"),
  };
}

const expand = names => names.includes("all") ? allNames() : names;

/** level(names, to, dur, curve) as a cue. */
export function handleLevelCue(ast) {
  const a = levelArgs(ast?.args);
  if (!a.names.length) { console.warn("[level] which sound? level(noise, 0, 12)"); return; }
  const to = a.to ?? a.numbers[0] ?? 1;
  const dur = Number(secs(a.dur ?? a.numbers[1] ?? 0));      // beats (beats.js)
  // *0.5 / +=0.2: from where each fader is (relative.js), kept within 0..MAX
  for (const n of expand(a.names)) {
    const t = isRel(to) ? Math.min(MAX, Math.max(0, applyRel(levelOf(n), to))) : Number(to);
    moveLevel(n, t, dur, a.curve || "lin");
  }
}

/** xfade(from, to, dur, curve) as a cue: the first down to 0, the second up to 1. */
export function handleXfadeCue(ast) {
  const a = levelArgs(ast?.args);
  const [out, into] = a.names;
  if (!out || !into) { console.warn("[xfade] two sounds: xfade(noise, bass, 8)"); return; }
  const dur = Number(secs(a.dur ?? a.numbers[0] ?? 4));      // beats (beats.js)
  moveLevel(out, 0, dur, a.curve || "cos");
  moveLevel(into, 1, dur, a.curve || "sin");
}
